import React, { useState, useEffect, useRef } from 'react';
import { ptzApi, presetApi } from '../../services/api';
import { useDriverFor } from '../../hooks/useCameraDrivers';
import { useAppStore } from '../../store/appStore';

const LABEL_ST = { fontFamily:'var(--font-mono)', fontSize:'10px', color:'rgba(255,255,255,0.62)', textTransform:'uppercase', letterSpacing:'0.06em', pointerEvents:'none', userSelect:'none' };

function pctToVal(pct, min, max) { return min + (max - min) * pct / 100; }
function valToPct(val, min, max) {
  if (max === min) return 50;
  return Math.min(100, Math.max(0, (val - min) / (max - min) * 100));
}

// Vertical slider track (tilt or zoom) — width:18, with rotated percentage inside
function VBar({ trackRef, label, pct, dragging, onDown, onMove, onUp, right, top = 8, bottom = 32, bg, fill, thumb, thumbShd }) {
  return (
    <div ref={trackRef} onPointerDown={onDown} onPointerMove={onMove} onPointerUp={onUp} onPointerCancel={onUp}
      style={{
        position:'absolute', right, top, bottom, width:18,
        background:bg, borderRadius:9, border:'1px solid rgba(255,255,255,0.12)',
        // Not a resize cursor: nothing here gets resized. These are value tracks — click to
        // jump, drag to sweep — so hover reads as clickable and the drag itself as grabbing.
        cursor: dragging ? 'grabbing' : 'pointer', zIndex:10, display:'flex', flexDirection:'column', alignItems:'center',
        touchAction:'none', userSelect:'none', overflow:'hidden',
      }}>
      {/* label */}
      <div style={{ ...LABEL_ST, marginTop:3, lineHeight:1 }}>{label}</div>
      {/* fill from bottom */}
      <div style={{
        position:'absolute', bottom:16, left:1, right:1, height:`${pct}%`,
        background:fill, borderRadius:'0 0 7px 7px',
        transition: dragging ? 'none' : 'height 0.15s',
      }} />
      {/* thumb */}
      <div style={{
        position:'absolute', bottom:`calc(${pct}% + 16px - 5px)`, left:-1, right:-1, height:9,
        background:thumb, borderRadius:5, boxShadow:thumbShd,
        transition: dragging ? 'none' : 'bottom 0.15s',
      }} />
      {/* percentage — rotated, centered in the bar */}
      <div style={{
        position:'absolute', top:20, bottom:16, left:0, right:0,
        display:'flex', alignItems:'center', justifyContent:'center',
        writingMode:'vertical-rl',
        fontSize:'10px', fontFamily:'var(--font-mono)', color:'rgba(255,255,255,0.85)',
        pointerEvents:'none', zIndex:3, lineHeight:1,
        textShadow:'0 0 3px rgba(0,0,0,0.9)',
        letterSpacing:'-0.01em',
      }}>
        {Math.round(pct)}%
      </div>
    </div>
  );
}

// onLayout reports how tall the bottom preset row currently is, so the chat and note
// launchers can sit directly above it. The row wraps, so this cannot be a constant: with
// enough presets, or a narrow enough window, it grows to two lines or more and a fixed
// offset would put the launchers back on top of it. Reports 0 when the presets are not
// along the bottom edge.
export default function OnScreenPTZ({ camera, onPresetActive, onLayout }) {
  const { addToast } = useAppStore();
  // Vendor conventions come from the driver descriptor, not from a brand check here.
  const driver = useDriverFor(camera);
  const THROTTLE = driver?.controls.minCommandIntervalMs ?? 150;

  const [position, setPosition]   = useState({ pan:0, tilt:0, zoom:0 });
  const [limits, setLimits]       = useState(null);
  const [presets, setPresets]     = useState([]);
  const [showSettings, setShowSettings] = useState(false);
  const [activePresetId, setActivePresetId] = useState(null);

  const posRef      = useRef({ pan:0, tilt:0, zoom:0 });
  const lastSent    = useRef(0);
  const pollRef     = useRef(null);

  const panDrag     = useRef(false);
  const tiltDrag    = useRef(false);
  const zoomDrag    = useRef(false);
  const [panPct,  setPanPct]  = useState(null);
  const [tiltPct, setTiltPct] = useState(null);
  const [zoomPct, setZoomPct] = useState(null);

  const panTrackRef  = useRef(null);
  const tiltTrackRef = useRef(null);
  const zoomTrackRef = useRef(null);

  const storKey = k => `studio_onscr_${k}_${camera.id}`;
  const [opacity,      setOpacity]      = useState(() => parseInt(localStorage.getItem(storKey('op'))  ?? '80'));
  const [sliderOpacity, setSliderOpacity] = useState(() => parseInt(localStorage.getItem(storKey('so'))  ?? '70'));
  const [presetPos,    setPresetPos]    = useState(() => localStorage.getItem(storKey('pp')) ?? 'bottom');

  // Effective zoom range. zoomEstimated limits are the camera's magnification caps, not
  // the scale it reports position on (an uncalibrated Dahua reports ~1-30x caps while
  // position runs ~4-97), so they are not usable as a range at all — seeding from them
  // strands the floor below the real floor, and observation only ever widens outward, so
  // it can never correct. Observe instead; it converges once both ends have been visited.
  // Calibrated ranges are exact and stay fixed. Run Calibrate Zoom to get one.
  const limitsRef    = useRef(null);
  const zoomObserved = useRef(null); // { min, max }

  useEffect(() => {
    limitsRef.current = limits;
    if (limits && !limits.zoomEstimated) zoomObserved.current = { min: limits.minZoom, max: limits.maxZoom };
  }, [limits]);

  function zoomRange() {
    const obs = zoomObserved.current;
    if (obs && obs.max > obs.min) return obs;
    if (limits && !limits.zoomEstimated) return { min: limits.minZoom, max: limits.maxZoom };
    return null; // range not established yet
  }

  // Dahua reports position proportional to magnification, so a linear bar crushes the
  // perceptible range into the bottom few percent (3.1x of 30x reads 7%, while the field
  // of view is already a third of wide). Map by ratio: equal travel = equal FOV ratio.
  const isLogZoom = (r) => !!limits?.zoomLogScale && r && r.min > 0 && r.max > r.min;
  const zoomValToPct = (val, r) => isLogZoom(r)
    ? Math.min(100, Math.max(0, Math.log(Math.max(val, r.min) / r.min) / Math.log(r.max / r.min) * 100))
    : valToPct(val, r.min, r.max);
  const zoomPctToVal = (pct, r) => isLogZoom(r)
    ? r.min * Math.pow(r.max / r.min, pct / 100)
    : pctToVal(pct, r.min, r.max);

  useEffect(() => {
    ptzApi.ptzLimits(camera.id).then(r => setLimits(r.data)).catch(() => {});
    loadPos();
    presetApi.list(camera.id).then(r => setPresets(r.data)).catch(() => {});
    pollRef.current = setInterval(() => {
      if (!panDrag.current && !tiltDrag.current && !zoomDrag.current) loadPos();
    }, 2000);
    return () => clearInterval(pollRef.current);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [camera.id]);

  // Guard against pile-up: a remote camera can take longer to answer than the poll
  // interval, and overlapping in-flight polls only add load, push responses past the
  // request timeout, and stall the readout for far longer than the interval.
  const posInFlight = useRef(false);

  // Returns the position it read, or null if it could not read one. The caller needs the
  // fresh value rather than posRef, because sendAbs writes the *commanded* value into posRef
  // optimistically — comparing against that would always look like instant arrival.
  async function loadPos() {
    if (posInFlight.current) return null;
    posInFlight.current = true;
    try {
      const r = await ptzApi.ptzPosition(camera.id);
      const z = r.data.zoom;
      if (limitsRef.current?.zoomEstimated && z != null && !isNaN(z)) {
        const obs = zoomObserved.current;
        if (!obs) zoomObserved.current = { min: z, max: z };
        else {
          if (z < obs.min) obs.min = z;
          if (z > obs.max) obs.max = z;
        }
      }
      posRef.current = r.data;
      setPosition(r.data);
      return r.data;
    } catch { return null; }
    finally { posInFlight.current = false; }
  }

  // force skips the throttle. The throttle exists to stop a drag flooding the camera, but it
  // must not swallow the first press or the final value on release: dropping the press makes a
  // click do nothing, and dropping the release leaves the camera wherever the last un-throttled
  // sample happened to be rather than where the operator let go.
  async function sendAbs(updates, { force = false } = {}) {
    const now = Date.now();
    if (!force && now - lastSent.current < THROTTLE) return;
    lastSent.current = now;
    const p = posRef.current;
    try {
      // Axis applies only the axes it's given; Dahua and Hikvision set all three from
      // the request, so send the full merged position for those.
      // Which axes have to be supplied is the driver's business, not this component's.
      // Some protocols apply only the axes given; others carry all three in one command, and
      // of those, some can be told to hold a zoom the client leaves out — which matters,
      // because restating a client-cached zoom is a real zoom move.
      const abs = driver?.absolute ?? { partial: false, holdsOmittedZoom: false };
      if (abs.partial) {
        await ptzApi.ptzAbsolute(camera.id, updates);
      } else {
        const body = { pan: updates.pan ?? p.pan, tilt: updates.tilt ?? p.tilt };
        if (updates.zoom != null || !abs.holdsOmittedZoom) body.zoom = updates.zoom ?? p.zoom;
        await ptzApi.ptzAbsolute(camera.id, body);
      }
      posRef.current = { ...p, ...updates };
    } catch {}
  }

  function getTrackPct(e, ref, axis) {
    const r = ref.current.getBoundingClientRect();
    if (axis === 'h') return Math.min(100, Math.max(0, (e.clientX - r.left) / r.width  * 100));
    return Math.min(100, Math.max(0, (1 - (e.clientY - r.top) / r.height) * 100));
  }
  // Hold the thumb where the operator put it until the camera is actually there.
  //
  // Previously onUp cleared the local percentage immediately, so the readout fell straight
  // back to `position` — which still held the pre-move value, because nothing had polled yet.
  // The thumb therefore jumped to the click, snapped back to where it started, and only
  // reached the new spot when the 3s background poll happened to catch up. The lone
  // setTimeout(loadPos, 400) did not help: 400ms is far shorter than these moves take, so it
  // read the old position too.
  const pendingTarget = useRef({});   // axisKey -> value last commanded
  const settleTimers  = useRef({});   // axisKey -> interval id

  useEffect(() => () => { Object.values(settleTimers.current).forEach(clearInterval); }, []);

  function watchArrival(axisKey, setPct) {
    clearInterval(settleTimers.current[axisKey]);
    const TOL = axisKey === 'zoom' ? 1.5 : 2;   // mm for zoom, degrees for pan and tilt
    const deadline = Date.now() + 12000;        // a move that never lands must not pin the thumb
    const near = (a, b) => {
      const d = Math.abs(a - b);
      return axisKey === 'pan' ? Math.min(d, 360 - d) <= TOL : d <= TOL;  // pan wraps at 360
    };
    settleTimers.current[axisKey] = setInterval(async () => {
      const fresh = await loadPos();            // null when a poll was already in flight
      const target = pendingTarget.current[axisKey];
      const cur = fresh?.[axisKey];
      const arrived = typeof cur === 'number' && typeof target === 'number' && near(cur, target);
      if (arrived || Date.now() > deadline) {
        clearInterval(settleTimers.current[axisKey]);
        delete settleTimers.current[axisKey];
        setPct(null);                           // readout goes back to tracking the camera
      }
    }, 500);
  }

  function makeHandlers(dragRef, setPct, axisKey, refKey, trackRef, isHoriz, rangeFn, toValFn, flip) {
    const range = () => rangeFn
      ? rangeFn()
      : (limits ? { min: limits[`min${refKey}`], max: limits[`max${refKey}`] } : null);
    const send = (pct, opts) => {
      const r = range();
      if (!r) return;
      const t = flip ? 100 - pct : pct;
      const val = toValFn ? toValFn(t, r) : pctToVal(t, r.min, r.max);
      pendingTarget.current[axisKey] = val;
      sendAbs({ [axisKey]: val }, opts);
    };
    return {
      onDown(e) {
        e.preventDefault(); dragRef.current = true;
        e.currentTarget.setPointerCapture(e.pointerId);
        clearInterval(settleTimers.current[axisKey]);   // a new gesture supersedes the old watch
        const pct = getTrackPct(e, trackRef, isHoriz ? 'h' : 'v');
        setPct(pct);
        send(pct, { force: true });   // a press must always reach the camera
      },
      onMove(e) {
        if (!dragRef.current) return;
        const pct = getTrackPct(e, trackRef, isHoriz ? 'h' : 'v');
        setPct(pct);
        send(pct);                    // throttled: this is the flood-prone path
      },
      onUp(e) {
        if (!dragRef.current) return;
        dragRef.current = false;
        // Re-send where the operator actually let go. The throttle may have dropped the last
        // few onMove samples, which would otherwise leave the camera short of the release
        // point with no further command coming.
        const pct = e ? getTrackPct(e, trackRef, isHoriz ? 'h' : 'v') : null;
        if (pct != null) { setPct(pct); send(pct, { force: true }); }
        watchArrival(axisKey, setPct);
      },
    };
  }

  // Some cameras count an axis opposite to the way these bars read — Dahua's tilt, for
  // instance, counts downward from the horizon, so its 90 means "look down". The driver says
  // which; this file does not need to know which brands those are.
  //
  // The flip applies to the outgoing value and the incoming display together. Flipping only
  // the command would leave the thumb chasing the camera in the opposite direction to the
  // drag, which reads as the control fighting you.
  const flipPan  = !!driver?.controls.invertPan;
  const flipTilt = !!driver?.controls.invertTilt;

  const panH  = makeHandlers(panDrag,  setPanPct,  'pan',  'Pan',  panTrackRef,  true,  null, null, flipPan);
  const tiltH = makeHandlers(tiltDrag, setTiltPct, 'tilt', 'Tilt', tiltTrackRef, false, null, null, flipTilt);
  const zoomH = makeHandlers(zoomDrag, setZoomPct, 'zoom', 'Zoom', zoomTrackRef, false, zoomRange, zoomPctToVal);

  const zr = zoomRange();
  const trackPct = (v, min, max, flip) => {
    const raw = valToPct(v, min, max);
    return flip ? 100 - raw : raw;
  };
  const displayPan  = panPct  ?? (limits ? trackPct(position.pan,  limits.minPan,  limits.maxPan,  flipPan)  : 50);
  const displayTilt = tiltPct ?? (limits ? trackPct(position.tilt, limits.minTilt, limits.maxTilt, flipTilt) : 50);
  const displayZoom = zoomPct ?? (zr ? zoomValToPct(position.zoom, zr) : 0);

  // Opacity-aware slider colors
  const sA  = sliderOpacity / 100;
  const sliderBg    = `rgba(8,12,18,${(sA * 0.78).toFixed(2)})`;
  const sliderFill  = `rgba(244,123,32,${(sA * 0.55).toFixed(2)})`;
  const sliderThumb = `rgba(244,123,32,${Math.min(1, sA * 1.15).toFixed(2)})`;
  const sliderShd   = `0 0 6px rgba(244,123,32,${(sA * 0.7).toFixed(2)})`;

  async function gotoPreset(preset) {
    try {
      // Feedback goes out first: a converging recall only resolves once the camera has
      // arrived, which is seconds, and a button that sits dead until then reads as a
      // missed click.
      const name = preset.displayName || preset.name;
      setActivePresetId(preset.id);
      onPresetActive?.({ id: preset.id, name });
      addToast({ type:'info', message:`Moving to: ${name}` });

      // Locally-stored presets carry an absolute position; camera-side presets recall by
      // id. converge: see PresetGrid.gotoPreset — a preset recall must actually arrive.
      if (preset.pan != null && preset.tilt != null && preset.zoom != null) {
        await ptzApi.ptzAbsolute(camera.id, { pan: preset.pan, tilt: preset.tilt, zoom: preset.zoom, converge: true });
      } else {
        await ptzApi.gotoPreset(camera.id, preset.cameraPresetId ?? preset.id);
      }
      // The converging path has already arrived by the time it resolves; the by-id path
      // has not, so keep the settle delay for it.
      setTimeout(loadPos, 2000);
    } catch { addToast({ type:'error', message:'Failed to go to preset' }); }
  }

  // Preset overlay position styles
  const ppIsVert = presetPos === 'left' || presetPos === 'right';
  const presetListStyle = {
    position:'absolute', zIndex:5,
    display:'flex', flexWrap: ppIsVert ? 'nowrap' : 'wrap',
    flexDirection: ppIsVert ? 'column' : 'row',
    gap:4, padding:6,
    overflowY: ppIsVert ? 'auto' : 'visible',
    overflowX: ppIsVert ? 'visible' : 'auto',
    maxHeight: ppIsVert ? 'calc(100% - 52px)' : 'auto',
    maxWidth: ppIsVert ? 120 : 'calc(100% - 70px)',
    // Bottom row sits just above the pan bar, with the chat and note launchers stacked above
    // it — the launchers move to clear this row rather than the other way round, because this
    // row is the one that changes height when it wraps. CameraView positions them from the
    // height reported through onLayout.
    //
    // The left/right columns keep a 74px gap instead: they run the full height of the frame,
    // so they have to end above the launchers, which sit at 30 when no bottom row exists.
    ...(presetPos === 'bottom' ? { bottom:31, left:0, right:58 } : {}),
    ...(presetPos === 'top'    ? { top:0,    left:0, right:0  } : {}),
    ...(presetPos === 'left'   ? { left:0,   top:0,  bottom:81 } : {}),
    ...(presetPos === 'right'  ? { right:58, top:0,  bottom:81 } : {}),
  };

  // Measure the bottom preset row and report it upward. ResizeObserver rather than a
  // presets.length calculation, because what matters is the rendered height after wrapping,
  // which depends on the frame width and the length of the preset names.
  const presetListRef = useRef(null);
  useEffect(() => {
    if (!onLayout) return;
    if (presetPos !== 'bottom') { onLayout({ bottomPresetsHeight: 0 }); return; }
    const el = presetListRef.current;
    if (!el) { onLayout({ bottomPresetsHeight: 0 }); return; }
    const report = () => onLayout({ bottomPresetsHeight: el.offsetHeight });
    report();
    const ro = new ResizeObserver(report);
    ro.observe(el);
    return () => ro.disconnect();
  }, [onLayout, presetPos, presets.length]);

  const btnAlpha = Math.max(0.05, opacity / 100 * 0.88);
  const txtAlpha = Math.min(1, opacity / 70);

  return (
    <>
      {/* ── Two vertical bars (right side) ── */}
      <VBar trackRef={tiltTrackRef} label="T" pct={displayTilt} dragging={tiltDrag.current}
        onDown={tiltH.onDown} onMove={tiltH.onMove} onUp={tiltH.onUp} right={30}
        bg={sliderBg} fill={sliderFill} thumb={sliderThumb} thumbShd={sliderShd} />
      <VBar trackRef={zoomTrackRef} label="Z" pct={displayZoom} dragging={zoomDrag.current}
        onDown={zoomH.onDown} onMove={zoomH.onMove} onUp={zoomH.onUp} right={6}
        bg={sliderBg} fill={sliderFill} thumb={sliderThumb} thumbShd={sliderShd} />

      {/* ── Pan bar (bottom, height:18) ── */}
      <div ref={panTrackRef}
        onPointerDown={panH.onDown} onPointerMove={panH.onMove} onPointerUp={panH.onUp} onPointerCancel={panH.onUp}
        style={{
          position:'absolute', bottom:6, left:6, right:58, height:18,
          background:sliderBg, borderRadius:9, border:'1px solid rgba(255,255,255,0.12)',
          cursor: panDrag.current ? 'grabbing' : 'pointer', zIndex:10, touchAction:'none', userSelect:'none', overflow:'hidden',
        }}>
        {/* fill */}
        <div style={{
          position:'absolute', top:1, bottom:1, left:1, width:`${displayPan}%`,
          background:sliderFill, borderRadius:'8px 0 0 8px',
          transition: panDrag.current ? 'none' : 'width 0.15s',
        }} />
        {/* thumb */}
        <div style={{
          position:'absolute', top:-1, bottom:-1, left:`calc(${displayPan}% - 4.5px)`, width:9,
          background:sliderThumb, borderRadius:4.5, boxShadow:sliderShd,
          transition: panDrag.current ? 'none' : 'left 0.15s',
        }} />
        {/* percentage centered */}
        <div style={{
          position:'absolute', top:'50%', left:'50%', transform:'translate(-50%,-50%)',
          fontSize:'10px', fontFamily:'var(--font-mono)', color:'rgba(255,255,255,0.88)',
          pointerEvents:'none', zIndex:3, lineHeight:1, whiteSpace:'nowrap',
          textShadow:'0 0 3px rgba(0,0,0,0.9)', letterSpacing:'0.02em',
        }}>
          PAN {Math.round(displayPan)}%
        </div>
      </div>

      {/* ── Preset overlay ── */}
      {presets.length > 0 && (
        <div ref={presetListRef} style={presetListStyle}>
          {presets.map(p => {
            const isActive = activePresetId === p.id;
            return (
              <button key={p.id} onClick={() => gotoPreset(p)}
                style={{
                  padding:'4px 10px', cursor:'pointer', whiteSpace:'nowrap',
                  background:`rgba(8,12,18,${btnAlpha})`,
                  border:`1px solid ${isActive ? 'rgba(0,255,136,0.7)' : 'rgba(244,123,32,0.4)'}`,
                  borderRadius:'var(--radius-sm)',
                  color:`rgba(255,255,255,${txtAlpha})`,
                  fontFamily:'var(--font-display)', fontSize:'10px', fontWeight:600, letterSpacing:'0.06em',
                  boxShadow: isActive ? '0 0 6px rgba(0,255,136,0.35)' : 'none',
                  transition:'border-color 0.15s, box-shadow 0.15s',
                }}
                onMouseEnter={e => { if (!isActive) e.currentTarget.style.borderColor='var(--accent-orange)'; }}
                onMouseLeave={e => { if (!isActive) e.currentTarget.style.borderColor='rgba(244,123,32,0.4)'; }}
              >
                {isActive && <span style={{ color:'var(--accent-green)', marginRight:4 }}>●</span>}
                {p.displayName || p.name}
              </button>
            );
          })}
        </div>
      )}

      {/* ── Settings gear ── */}
      <button onClick={() => setShowSettings(v => !v)}
        style={{
          position:'absolute', top:6, left:6, width:22, height:22, zIndex:15,
          background:sliderBg, border:'1px solid rgba(255,255,255,0.15)', borderRadius:5,
          color:'rgba(255,255,255,0.6)', cursor:'pointer',
          display:'flex', alignItems:'center', justifyContent:'center',
        }}>
        <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
          <circle cx="12" cy="12" r="3"/>
          <path d="M19.4 15a1.65 1.65 0 00.33 1.82l.06.06a2 2 0 010 2.83 2 2 0 01-2.83 0l-.06-.06a1.65 1.65 0 00-1.82-.33 1.65 1.65 0 00-1 1.51V21a2 2 0 01-4 0v-.09A1.65 1.65 0 009 19.4a1.65 1.65 0 00-1.82.33l-.06.06a2 2 0 01-2.83-2.83l.06-.06A1.65 1.65 0 004.68 15a1.65 1.65 0 00-1.51-1H3a2 2 0 010-4h.09A1.65 1.65 0 004.6 9a1.65 1.65 0 00-.33-1.82l-.06-.06a2 2 0 012.83-2.83l.06.06A1.65 1.65 0 009 4.68a1.65 1.65 0 001-1.51V3a2 2 0 014 0v.09a1.65 1.65 0 001 1.51 1.65 1.65 0 001.82-.33l.06-.06a2 2 0 012.83 2.83l-.06.06A1.65 1.65 0 0019.4 9a1.65 1.65 0 001.51 1H21a2 2 0 010 4h-.09a1.65 1.65 0 00-1.51 1z"/>
        </svg>
      </button>

      {/* ── Settings panel ── */}
      {showSettings && (
        <div style={{
          position:'absolute', top:34, left:6, width:210, zIndex:20,
          background:'rgba(8,12,18,0.96)', border:'1px solid var(--border-bright)',
          borderRadius:'var(--radius-md)', padding:14, backdropFilter:'blur(12px)',
        }}>
          <div style={{ fontFamily:'var(--font-display)', fontSize:'10px', fontWeight:700, letterSpacing:'0.1em', textTransform:'uppercase', color:'var(--text-muted)', marginBottom:12 }}>
            On-Screen Settings
          </div>

          {/* Slider opacity */}
          <div style={{ marginBottom:12 }}>
            <div style={{ fontFamily:'var(--font-mono)', fontSize:'10px', color:'var(--text-muted)', marginBottom:4 }}>
              Slider Opacity: {sliderOpacity}%
            </div>
            <input type="range" min="0" max="100" value={sliderOpacity} style={{ width:'100%', accentColor:'var(--accent-orange)' }}
              onChange={e => { const v = parseInt(e.target.value); setSliderOpacity(v); localStorage.setItem(storKey('so'), v); }} />
          </div>

          {/* Preset opacity */}
          <div style={{ marginBottom:12 }}>
            <div style={{ fontFamily:'var(--font-mono)', fontSize:'10px', color:'var(--text-muted)', marginBottom:4 }}>
              Preset Opacity: {opacity}%
            </div>
            <input type="range" min="0" max="100" value={opacity} style={{ width:'100%', accentColor:'var(--accent-orange)' }}
              onChange={e => { const v = parseInt(e.target.value); setOpacity(v); localStorage.setItem(storKey('op'), v); }} />
          </div>

          {/* Preset position */}
          <div>
            <div style={{ fontFamily:'var(--font-mono)', fontSize:'10px', color:'var(--text-muted)', marginBottom:6 }}>Preset Position</div>
            <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:4 }}>
              {['top','bottom','left','right'].map(pos => (
                <button key={pos} onClick={() => { setPresetPos(pos); localStorage.setItem(storKey('pp'), pos); }}
                  style={{
                    padding:'4px 6px', cursor:'pointer', textTransform:'capitalize',
                    background: presetPos === pos ? 'rgba(244,123,32,0.18)' : 'var(--bg-surface)',
                    border:`1px solid ${presetPos === pos ? 'var(--accent-orange)' : 'var(--border-dim)'}`,
                    borderRadius:'var(--radius-sm)',
                    color: presetPos === pos ? 'var(--accent-orange)' : 'var(--text-secondary)',
                    fontFamily:'var(--font-mono)', fontSize:'10px',
                  }}
                >{pos}</button>
              ))}
            </div>
          </div>
        </div>
      )}
    </>
  );
}
